/**
 * Google Maps config, kept out of the component file so react-refresh is not
 * asked to hot-swap a module that exports both components and constants.
 *
 * The API key is the only hard requirement. `MAP_ID` is a Google Cloud map id
 * (a styling profile, not a key) and is optional here - see
 * `CLARA_VALE_MAP_STYLES` for why a map that depends on one is a map that
 * disappears on every machine without it.
 */

export const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''
export const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID ?? ''

/**
 * Which config value is missing, or null when a map can render.
 *
 * Only the API key is required. A map id is optional - it buys Cloud-hosted
 * styling and `AdvancedMarker`, and its absence must not cost you the map.
 */
export function missingMapConfig(): string | null {
  if (!MAPS_KEY) return 'VITE_GOOGLE_MAPS_API_KEY'
  return null
}

/**
 * One hue per day of a trip, cycling. Deliberately literal colours rather
 * than CSS variables: these are painted onto the Maps canvas and into marker
 * DOM outside our stylesheet's cascade. Drawn from the platform's palette so
 * a map still reads as ours.
 */
const DAY_COLOURS = [
  '#1c5a48', // green
  '#b5176b', // magenta
  '#8a6d3b', // ochre
  '#3d5a80', // slate blue
  '#7d3c98', // plum
]

export function dayColour(dayIndex: number): string {
  return DAY_COLOURS[(Math.max(1, dayIndex) - 1) % DAY_COLOURS.length]
}

/**
 * Clara Vale basemap, as classic style JSON.
 *
 * The Scheduling Console styles its map through a Google Cloud **Map ID**,
 * which means the styling lives outside this repo and the map silently
 * degrades to "unavailable" anywhere that id is not configured - which is
 * every local machine, because the id only exists as a CI secret. A demo or a
 * local test therefore loses the map entirely.
 *
 * Styling inline costs a block of JSON and buys a map that renders wherever
 * the API key does. Muted and warm on purpose: the route and its pins are the
 * information, and the basemap should stay out of their way.
 */
export const CLARA_VALE_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f6f4ef' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6b6b63' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f6f4ef' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d9d5cb' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#eeece4' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#f0eee7' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e6e2d8' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#cdd8d6' }] },
]
